// What the app shell needs on its first screen: who's signed in, what the server can do, the
// repositories, and the inbox. Everything else lives in platform-data.ts, loaded with the views
// that use it.

import { withRepository } from "./repo-cache";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { create } from "@bufbuild/protobuf";
import { isUnimplemented } from "./api/client";
import { SERVER_HOST, useConnection, usePlatformApi } from "./connection";
import { RepositorySchema, type Repository } from "./gen/gd_platform_pb";

/**
 * Servers without the platform service (one repository, today's grovedockd) answer Unimplemented.
 * Every platform read then falls back to what such a server is: one repository, no reviews.
 */
export async function or<T, F>(call: Promise<T>, fallback: F): Promise<T | F> {
  try {
    return await call;
  } catch (e) {
    if (isUnimplemented(e)) return fallback;
    throw e;
  }
}

/** The one repository of a server without the platform service. */
export const SINGLE = { owner: "server", name: "repo" };
export const isSingle = (key: string) => key === `${SINGLE.owner}/${SINGLE.name}`;

function singleRepository(label: string): Repository {
  return create(RepositorySchema, { owner: SINGLE.owner, name: SINGLE.name, description: `The repository on ${label}.`, defaultBranch: "main" });
}

export const ref = (key: string) => {
  const [owner = "", name = ""] = key.split("/");
  return { owner, name };
};

/**
 * Who is signed in and which organisations they belong to. A server without the platform
 * service (one repository, older version) answers Unimplemented: the app then shows that one
 * repository on its own.
 */
export function useViewer() {
  const api = usePlatformApi();
  const { signedIn } = useConnection();
  return useQuery({
    queryKey: ["viewer"],
    enabled: signedIn,
    queryFn: async () => {
      try {
        const v = await api.platform.viewer({});
        return { platform: true as const, ...v, principal: v.account?.login ?? "" };
      } catch (e) {
        if (isUnimplemented(e)) {
          const who = await api.access.whoAmI({}).catch(() => null);
          return { platform: false as const, account: undefined, principal: who?.principal ?? "", organizations: [] };
        }
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

/**
 * What the connected server can do. Hosting many repositories and reviewing change requests
 * ship separately; the app shows only what the server answers.
 */
export function useCapabilities() {
  const api = usePlatformApi();
  const { signedIn } = useConnection();
  return useQuery({
    queryKey: ["capabilities", signedIn],
    queryFn: async () => {
      let reviews = true;
      // A signed-out visitor has no inbox; servers that host many repositories review.
      if (signedIn) {
        try {
          await api.reviews.inbox({ limit: 1 });
        } catch (e) {
          if (isUnimplemented(e)) reviews = false;
          else throw e;
        }
      }
      // Plans (ADR-035): where to upgrade, and whether organisations have limits.
      const plans = await or(api.platform.getCapabilities({}), { billingUrl: "", limits: false, supportUrl: "", gitHttp: false });
      return { reviews, billingUrl: plans.billingUrl, limits: plans.limits, supportUrl: plans.supportUrl, gitHttp: plans.gitHttp };
    },
    staleTime: 5 * 60_000,
  });
}

export function useRepositories(owner = "") {
  const api = usePlatformApi();
  const label = SERVER_HOST;
  return useQuery({
    queryKey: ["repos", owner],
    // Without an owner: the repositories you're involved in, not every public one you could read.
    queryFn: async () => (await or(api.platform.listRepositories({ owner, limit: 200, involved: !owner }), { repositories: [singleRepository(label)] })).repositories,
    refetchInterval: 10_000,
  });
}

export function useRepository(key: string) {
  const api = usePlatformApi();
  const label = SERVER_HOST;
  const qc = useQueryClient();
  return useQuery({
    queryKey: ["repo", key],
    enabled: !!key,
    queryFn: async () => {
      const r = await or(api.platform.getRepository(ref(key)), singleRepository(label));
      // One number everywhere: the sidebar and organisation lists show this repository as just read.
      qc.setQueriesData({ queryKey: ["repos"] }, (data: unknown) => withRepository(data, r));
      return r;
    },
    refetchInterval: 10_000,
    retry: false,
  });
}


export function useInbox() {
  const api = usePlatformApi();
  const { signedIn } = useConnection();
  return useQuery({ enabled: signedIn, queryKey: ["inbox"], queryFn: async () => (await or(api.reviews.inbox({ limit: 100 }), { items: [] })).items, refetchInterval: 5000 });
}
